import React, { useState, useRef, useMemo, useCallback, useEffect } from 'react';
import './styles/index.css';
import Logo26 from '../../assets/Logo26.png';
import api from './api';
import { getBracketFormatLabel } from './utils/championshipFormat';

// Constantes del bracket - tamaños fijos para mantener alineaciones
const CARD_HEIGHT = 90;
const CARD_WIDTH = 180;
const HORIZONTAL_GAP = 70;
const VERTICAL_GAP = 12;
const CONNECTOR_LINE_LENGTH = 25;
const LINE_THICKNESS = 2;
const TITULO_ALTURA = 40;
const COLUMN_GAP = 30;

const getEntityId = (value) => (typeof value === 'object' ? value?._id : value);

const formatMatchStatValue = (value, options = {}) => {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return options.fallback || '-';
  }

  const decimals = options.decimals ?? 0;
  const suffix = options.suffix ?? '';
  return `${value.toFixed(decimals)}${suffix}`;
};

const BracketViewer = ({ bracketData, userId, campeonatoNombre }) => {
  const [modoVista, setModoVista] = useState('winner');
  const [partidaSeleccionada, setPartidaSeleccionada] = useState(null);
  const [detallePartida, setDetallePartida] = useState(null);
  const [loadingDetallePartida, setLoadingDetallePartida] = useState(false);
  const [showCampeonModal, setShowCampeonModal] = useState(false);
  const scrollContainerRef = useRef(null);
  const detalleRequestRef = useRef(0);
  const detalleCacheRef = useRef(new Map());

  const layoutVars = useMemo(() => ({
    '--bracket-scale': 1,
    '--card-height': `${CARD_HEIGHT}px`,
    '--card-width': `${CARD_WIDTH}px`,
    '--card-padding': '8px',
    '--horizontal-gap': `${HORIZONTAL_GAP}px`,
    '--vertical-gap': `${VERTICAL_GAP}px`,
    '--title-height': `${TITULO_ALTURA}px`,
    '--line-thickness': `${LINE_THICKNESS}px`,
    '--column-gap': `${COLUMN_GAP}px`,
    '--font-scale': 1,
    '--scroll-max-height': '75vh'
  }), []);

  // Verificar si un jugador es el usuario actual
  const esUsuario = useCallback((jugadorId) => {
    if (!userId) return false;
    const idString = typeof jugadorId === 'object' ? jugadorId?._id : jugadorId;
    return String(idString) === String(userId);
  }, [userId]);

  const closePartidaModal = useCallback(() => {
    detalleRequestRef.current += 1;
    setPartidaSeleccionada(null);
    setDetallePartida(null);
    setLoadingDetallePartida(false);
  }, []);

  const cargarDetallePartida = useCallback(async (partida) => {
    if (!partida) {
      return;
    }

    const requestId = detalleRequestRef.current + 1;
    detalleRequestRef.current = requestId;

    setPartidaSeleccionada(partida);
    setDetallePartida(null);

    if (!partida.partidaId) {
      setLoadingDetallePartida(false);
      return;
    }

    const detalleCacheado = detalleCacheRef.current.get(partida.partidaId);
    if (detalleCacheado) {
      setDetallePartida(detalleCacheado);
      setLoadingDetallePartida(false);
      return;
    }

    setLoadingDetallePartida(true);

    try {
      const data = await api.getPartidaDetalle(partida.partidaId);
      if (detalleRequestRef.current === requestId) {
        detalleCacheRef.current.set(partida.partidaId, data);
        setDetallePartida(data);
      }
    } catch (error) {
      console.error('Error cargando detalle de partida de campeonato:', error);
      if (detalleRequestRef.current === requestId) {
        setDetallePartida(null);
      }
    } finally {
      if (detalleRequestRef.current === requestId) {
        setLoadingDetallePartida(false);
      }
    }
  }, []);

  useEffect(() => () => {
    detalleRequestRef.current += 1;
    detalleCacheRef.current.clear();
  }, []);

  useEffect(() => {
    detalleCacheRef.current.clear();
  }, [bracketData]);



  // =====================================================
  // FUNCIÓN PARA EXTRAER EL NÚMERO DE PARTIDA DEL LOSER DESDE destinoPerdedor
  // El campo viene del backend con formato: "L-R1-3-P1", "L-R2-1-P2", "L-Final-P2", etc.
  // =====================================================
  const extraerNumeroLoser = useCallback((destinoPerdedor, rondaNombre, bracketData) => {
    if (!destinoPerdedor || destinoPerdedor === 'ELIMINADO') {
      return null;
    }
    
    // Gran Final no tiene destino loser
    if (rondaNombre?.includes('Gran')) {
      return null;
    }
    
    // Si es L-Final, mostrar "LF"
    if (destinoPerdedor.includes('L-Final')) {
      return 'LF';
    }
    
    // Extraer ronda y partida del formato "L-R1-3-P1" → ronda=1, partidaNum=3
    const match = destinoPerdedor.match(/L-R(\d+)-(\d+)/);
    if (!match) return null;
    
    const rondaLoser = parseInt(match[1]);
    const partidaNumEnRonda = parseInt(match[2]);
    
    // Mapeo de L-Rx a nombre de ronda en frontend
    // L-R1 → "Loser A", L-R2 → "+ Loser B", L-R3 → "Loser R3", L-R4 → "+ Loser C", etc.
    const nombreRondaLoser = rondaLoser === 1 ? 'Loser A' 
      : rondaLoser % 2 === 0 ? `+ Loser ${'ABCDEFGH'[rondaLoser / 2]}`
      : `Loser R${rondaLoser}`;
    
    // Buscar la posición real de esa partida en el Loser bracket
    if (bracketData?.bracket?.loser?.rondas) {
      const rondasLoser = bracketData.bracket.loser.rondas;
      const partidas = rondasLoser[nombreRondaLoser];
      
      if (partidas && Array.isArray(partidas)) {
        // Ordenar por posición para asegurar orden correcto
        const partidasOrdenadas = [...partidas]
          .filter(p => p && p._id)
          .sort((a, b) => (a.posicion || 0) - (b.posicion || 0));
        
        if (partidaNumEnRonda <= partidasOrdenadas.length) {
          const partidaDestino = partidasOrdenadas[partidaNumEnRonda - 1];
          if (partidaDestino && partidaDestino.posicion) {
            return `L${partidaDestino.posicion}`;
          }
        }
      }
    }
    
    // Fallback: mostrar el formato simplificado
    return `L-R${rondaLoser}-${partidaNumEnRonda}`;
  }, []);

  // Determinar si es eliminación simple
  const esEliminacionSimple = useMemo(() => {
    return bracketData?.info?.tipoEliminacion === 'simple' ||
           (!bracketData?.bracket?.loser?.rondas || Object.keys(bracketData?.bracket?.loser?.rondas || {}).length === 0);
  }, [bracketData?.info?.tipoEliminacion, bracketData?.bracket?.loser?.rondas]);

  // Obtener y ordenar rondas según modo
  const rondas = useMemo(() => {
    if (!bracketData?.bracket) return [];
    
    const bracket = modoVista === 'winner' 
      ? bracketData.bracket.winner 
      : bracketData.bracket.loser;
    
    if (!bracket?.rondas) return [];
    
    let rondasArray = Object.entries(bracket.rondas)
      .map(([nombre, partidas]) => ({
        nombre,
        // Filtrar partidas válidas y ORDENAR POR POSICIÓN para orden visual correcto
        partidas: (partidas || [])
          .filter(p => p && p._id)
          .sort((a, b) => (a.posicion || 0) - (b.posicion || 0))
      }))
      .filter(r => r.partidas.length > 0);
    
    // En eliminación simple, filtrar la Gran Final (no existe, la Final Winner ES la final)
    if (esEliminacionSimple && modoVista === 'winner') {
      rondasArray = rondasArray.filter(r => r.nombre !== 'Gran Final');
    }
    
    return rondasArray.sort((a, b) => {
        // Ordenar correctamente las rondas
        const getOrden = (nombre) => {
          if (nombre === 'Gran Final') return 1000;
          if (nombre.includes('Final') && !nombre.includes('Gran')) return 999;
          // Winner bracket
          const matchRonda = nombre.match(/Ronda (\d+)/);
          if (matchRonda) return parseInt(matchRonda[1]);
          // Loser bracket
          if (nombre === 'Loser A') return 1;
          if (nombre === '+ Loser B') return 2;
          if (nombre === 'Loser R3') return 3;
          if (nombre === '+ Loser C') return 4;
          if (nombre === 'Loser R5') return 5;
          if (nombre === '+ Loser D') return 6;
          if (nombre === 'Loser R7') return 7;
          if (nombre === '+ Loser E') return 8;
          if (nombre === 'Loser R9') return 9;
          return 0;
        };
        return getOrden(a.nombre) - getOrden(b.nombre);
      });
  }, [bracketData, modoVista, esEliminacionSimple]);

  // Calcular multiplicador de espaciado para cada ronda
  const calcularMultiplicador = useCallback((rondaIdx, numPartidas, nombreRonda) => {
    const esLoserBracket = modoVista === 'loser';
    
    if (nombreRonda === 'Gran Final' && rondas[rondaIdx - 1]?.partidas?.length === 1) {
      return Math.pow(2, rondaIdx - 1);
    }
    
    if (!esLoserBracket) {
      return Math.pow(2, rondaIdx);
    }
    
    // Loser bracket: basarse en partidas de primera ronda
    const primeraRondaPartidas = rondas[0]?.partidas?.length || 16;
    const ratio = primeraRondaPartidas / numPartidas;
    return Math.max(1, ratio);
  }, [modoVista, rondas]);

  // Buscar posición del usuario
  const buscarMiPosicion = useCallback(() => {
    if (!userId) return;

    // Buscar en el bracket actual (desde última ronda hacia atrás)
    for (let ri = rondas.length - 1; ri >= 0; ri--) {
      const ronda = rondas[ri];
      for (let pi = 0; pi < ronda.partidas.length; pi++) {
        const partida = ronda.partidas[pi];
        if (esUsuario(partida.jugador1?._id) || esUsuario(partida.jugador2?._id)) {
          // Calcular posición y hacer scroll
          const multiplicador = calcularMultiplicador(ri, ronda.partidas.length, ronda.nombre);
          const espaciadoBase = CARD_HEIGHT + VERTICAL_GAP;
          const espaciadoRonda = espaciadoBase * multiplicador;
          const offsetInicial = (espaciadoRonda - CARD_HEIGHT) / 2;
          
          const xScroll = ri * (CARD_WIDTH + HORIZONTAL_GAP);
          const yScroll = TITULO_ALTURA + offsetInicial + (pi * espaciadoRonda);
          
          if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTo({
              left: Math.max(0, xScroll - 20),
              top: Math.max(0, yScroll - 50),
              behavior: 'smooth'
            });
          }
          return;
        }
      }
    }

    // Si no está en este bracket, buscar en el otro
    const otroBracket = modoVista === 'winner' 
      ? bracketData?.bracket?.loser?.rondas 
      : bracketData?.bracket?.winner?.rondas;
    
    if (otroBracket) {
      for (const [, partidas] of Object.entries(otroBracket)) {
        for (const partida of partidas || []) {
          if (esUsuario(partida?.jugador1?._id) || esUsuario(partida?.jugador2?._id)) {
            setModoVista(modoVista === 'winner' ? 'loser' : 'winner');
            return;
          }
        }
      }
    }
  }, [userId, rondas, modoVista, bracketData, esUsuario, calcularMultiplicador]);

  // Componente de tarjeta de partida
  const PartidaCard = ({ partida, esLoserBracket = false, partidaIndex = 0, rondaIndex = 0, rondaNombre = '' }) => {
    if (!partida || !partida._id) return null;
    
    const j1Nombre = partida.nombreJugador1 || partida.jugador1?.username || 'TBD';
    const j2Nombre = partida.nombreJugador2 || partida.jugador2?.username || 'TBD';
    
    const ganadorId = typeof partida.ganador === 'string' 
      ? partida.ganador 
      : partida.ganador?._id;
    
    const j1Id = partida.jugador1?._id;
    const j2Id = partida.jugador2?._id;
    
    const j1Ganador = ganadorId && String(ganadorId) === String(j1Id);
    const j2Ganador = ganadorId && String(ganadorId) === String(j2Id);
    
    const esJ1Usuario = esUsuario(j1Id);
    const esJ2Usuario = esUsuario(j2Id);
    const contieneUsuario = esJ1Usuario || esJ2Usuario;
    
    const esBye = partida.isBye;
    const estaCompletada = partida.completada;
    
    const score1 = partida.resultado?.jugador1Sets ?? '-';
    const score2 = partida.resultado?.jugador2Sets ?? '-';
    
    // Estadísticas de la partida (aceptar múltiples claves para compatibilidad)
    const stats = partida.estadisticasPartida || partida.estadisticas || partida.stats;
    const statsJ1 = stats?.jugador1 || {};
    const statsJ2 = stats?.jugador2 || {};
    const tieneStats = stats && (
      (statsJ1.average || 0) > 0 || (statsJ2.average || 0) > 0 ||
      (statsJ1.count180s || 0) > 0 || (statsJ2.count180s || 0) > 0
    );

    // Extraer número de partida del Loser desde destinoPerdedor (solo Winner Bracket)
    const numeroLoserDestino = !esLoserBracket ? extraerNumeroLoser(partida.destinoPerdedor, rondaNombre, bracketData) : null;

    const handleClick = () => {
      if (estaCompletada && !esBye) {
        cargarDetallePartida(partida);
      }
    };

    return (
      <div 
        className={`bracket-match-card 
          ${estaCompletada ? 'completada' : ''} 
          ${esBye ? 'bye' : ''} 
          ${contieneUsuario ? 'mi-partida' : ''}
          ${estaCompletada && !esBye ? 'clickeable' : ''}`}
        onClick={handleClick}
        title={estaCompletada && !esBye ? 'Click para ver estadísticas' : ''}
      >
        {/* Header con número de partida y destino */}
        <div className="match-header">
          <div className="match-header-left">
            <span className="match-position">#{partida.posicion ?? 0}</span>
            {/* Número de partida del Loser destino - SOLO en Winner Bracket */}
            {numeroLoserDestino && (
              <span className="destino-loser-badge" title={`Perdedor va a partida ${numeroLoserDestino}`}>
                → {numeroLoserDestino}
              </span>
            )}
          </div>
          <div className="match-badges">
            {contieneUsuario && <span className="badge-usuario">👤</span>}
            {tieneStats && <span className="badge-stats">📊</span>}
            {estaCompletada && !esBye && <span className="badge-completada">✓</span>}
            {esBye && <span className="badge-bye">BYE</span>}
          </div>
        </div>
        
        {/* Jugador 1 */}
        <div className={`match-player 
          ${esJ1Usuario ? 'es-usuario' : ''} 
          ${j1Ganador ? 'ganador' : ''} 
          ${estaCompletada && !j1Ganador && !esBye ? 'perdedor' : ''}`}
        >
          {esJ1Usuario && <span className="usuario-star">⭐</span>}
          <span className={`player-name ${j1Nombre === 'TBD' ? 'tbd' : ''}`}>
            {j1Nombre}
          </span>
          <span className={`player-score ${j1Ganador ? 'ganador' : ''}`}>
            {estaCompletada ? score1 : '-'}
          </span>
        </div>
        
        {/* Separador */}
        <div className="match-divider"></div>
        
        {/* Jugador 2 */}
        <div className={`match-player 
          ${esJ2Usuario ? 'es-usuario' : ''} 
          ${j2Ganador ? 'ganador' : ''} 
          ${estaCompletada && !j2Ganador && !esBye ? 'perdedor' : ''}`}
        >
          {esJ2Usuario && <span className="usuario-star">⭐</span>}
          <span className={`player-name ${j2Nombre === 'TBD' ? 'tbd' : ''}`}>
            {j2Nombre}
          </span>
          <span className={`player-score ${j2Ganador ? 'ganador' : ''}`}>
            {estaCompletada ? score2 : '-'}
          </span>
        </div>
      </div>
    );
  };

  // Calcular altura total necesaria
  const calcularAlturaBracket = useMemo(() => {
    if (rondas.length === 0) return 300;
    
    // La primera ronda determina la altura base
    const primeraRonda = rondas[0];
    const numPartidas = primeraRonda?.partidas?.length || 1;
    const espaciadoBase = CARD_HEIGHT + VERTICAL_GAP;
    
    // Altura = número de partidas * espaciado + margen
    return TITULO_ALTURA + (numPartidas * espaciadoBase) + 50;
  }, [rondas]);

  // Renderizar el bracket completo
  const renderBracket = () => {
    if (rondas.length === 0) {
      return (
        <div className="bracket-empty">
          <p>{modoVista === 'loser' 
            ? 'El Loser Bracket aún no tiene partidas' 
            : 'No hay partidas disponibles'}
          </p>
        </div>
      );
    }

    const espaciadoBase = CARD_HEIGHT + VERTICAL_GAP;

    return (
      <div className="bracket-content-wrapper">
        {/* Patrón de logos como fondo */}
        <div className="bracket-logo-pattern">
          {Array.from({ length: 30 }).map((_, i) => (
            <img 
              key={`logo-${i}`}
              src={Logo26}
              alt=""
              className="bracket-logo-item"
            />
          ))}
        </div>
        
        {rondas.map((ronda, rondaIndex) => {
          const numPartidas = ronda.partidas.length;
          const siguienteRonda = rondas[rondaIndex + 1];
          const siguienteNumPartidas = siguienteRonda?.partidas?.length || null;
          
          const multiplicador = calcularMultiplicador(rondaIndex, numPartidas, ronda.nombre);
          const espaciadoRonda = espaciadoBase * multiplicador;
          const offsetInicial = (espaciadoRonda - CARD_HEIGHT) / 2;
          const tieneConectores = rondaIndex < rondas.length - 1;
          const siguienteReduce = siguienteNumPartidas !== null && siguienteNumPartidas < numPartidas;
          
          return (
            <div key={`ronda-${rondaIndex}`} className="bracket-ronda-wrapper">
              {/* Columna de la Ronda */}
              <div className="bracket-ronda-column" style={{ width: CARD_WIDTH }}>
                {/* Título de ronda */}
                <div className="bracket-ronda-header" style={{ height: TITULO_ALTURA }}>
                  <span className="ronda-titulo">{ronda.nombre}</span>
                </div>
                
                {/* Partidas */}
                <div className="bracket-partidas-container">
                  {ronda.partidas.map((partida, idx) => {
                    if (!partida) return null;
                    const marginTop = idx === 0 ? offsetInicial : espaciadoRonda - CARD_HEIGHT;
                    
                    return (
                      <div 
                        key={partida._id || `p-${idx}`} 
                        className="bracket-partida-slot"
                        style={{ marginTop }}
                      >
                        <PartidaCard 
                          partida={partida} 
                          esLoserBracket={modoVista === 'loser'} 
                          partidaIndex={idx}
                          rondaIndex={rondaIndex}
                          rondaNombre={ronda.nombre}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
              
              {/* Conectores hacia siguiente ronda */}
              {tieneConectores && (
                <div className="bracket-conectores-column" style={{ width: HORIZONTAL_GAP }}>
                  <svg 
                    className="conectores-svg"
                    style={{ 
                      width: HORIZONTAL_GAP, 
                      height: calcularAlturaBracket,
                      position: 'absolute',
                      top: 0,
                      left: 0
                    }}
                  >
                    {ronda.partidas.map((partida, idx) => {
                      if (!partida) return null;
                      
                      // Posición Y del centro de cada card
                      const posCard = TITULO_ALTURA + offsetInicial + (idx * espaciadoRonda);
                      const centroCard = posCard + (CARD_HEIGHT / 2);
                      
                      // Color según estado
                      const colorLinea = partida?.completada ? '#00C6CF' : '#3A4556';
                      
                      // Si NO reduce partidas, conexión directa
                      if (!siguienteReduce) {
                        return (
                          <line
                            key={`conn-${idx}`}
                            x1="0"
                            y1={centroCard}
                            x2={HORIZONTAL_GAP}
                            y2={centroCard}
                            stroke={colorLinea}
                            strokeWidth={LINE_THICKNESS}
                          />
                        );
                      }
                      
                      // Si reduce (2→1), solo procesar pares
                      if (idx % 2 !== 0) return null;
                      
                      const partida2 = ronda.partidas[idx + 1];
                      const tienePareja = partida2 != null;
                      
                      const posCard2 = TITULO_ALTURA + offsetInicial + ((idx + 1) * espaciadoRonda);
                      const centroCard2 = tienePareja ? posCard2 + (CARD_HEIGHT / 2) : centroCard;
                      
                      const puntoMedio = (centroCard + centroCard2) / 2;
                      
                      const p1Completada = partida?.completada;
                      const p2Completada = partida2?.completada;
                      const colorActivo = '#00C6CF';
                      const colorInactivo = '#3A4556';
                      
                      return (
                        <g key={`conn-${idx}`}>
                          {/* Línea horizontal desde partida 1 */}
                          <line
                            x1="0"
                            y1={centroCard}
                            x2={CONNECTOR_LINE_LENGTH}
                            y2={centroCard}
                            stroke={p1Completada ? colorActivo : colorInactivo}
                            strokeWidth={LINE_THICKNESS}
                          />
                          
                          {tienePareja && (
                            <>
                              {/* Línea horizontal desde partida 2 */}
                              <line
                                x1="0"
                                y1={centroCard2}
                                x2={CONNECTOR_LINE_LENGTH}
                                y2={centroCard2}
                                stroke={p2Completada ? colorActivo : colorInactivo}
                                strokeWidth={LINE_THICKNESS}
                              />
                              
                              {/* Línea vertical conectando ambas */}
                              <line
                                x1={CONNECTOR_LINE_LENGTH}
                                y1={centroCard}
                                x2={CONNECTOR_LINE_LENGTH}
                                y2={centroCard2}
                                stroke={(p1Completada && p2Completada) ? colorActivo : colorInactivo}
                                strokeWidth={LINE_THICKNESS}
                              />
                            </>
                          )}
                          
                          {/* Línea horizontal hacia siguiente ronda */}
                          <line
                            x1={CONNECTOR_LINE_LENGTH}
                            y1={puntoMedio}
                            x2={HORIZONTAL_GAP}
                            y2={puntoMedio}
                            stroke={(p1Completada && (p2Completada || !tienePareja)) ? colorActivo : colorInactivo}
                            strokeWidth={LINE_THICKNESS}
                          />
                        </g>
                      );
                    })}
                  </svg>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // =====================================================
  // LÓGICA DEL CAMPEÓN - Compatible con doble y simple eliminación
  // En doble eliminación: el campeón viene de granFinal.ganador
  // En eliminación simple: el campeón viene de bracketData/campeonato resultados
  // =====================================================
  
  // Ganador de la Gran Final (doble eliminación)
  const granFinal = bracketData?.bracket?.granFinal;
  const ganadorGranFinalId = typeof granFinal?.ganador === 'string'
    ? granFinal?.ganador
    : granFinal?.ganador?._id;

  const ganadorGranFinalNombre = (() => {
    if (!granFinal) return null;
    if (granFinal?.ganador?.username || granFinal?.ganador?.nombre) {
      return granFinal.ganador.username || granFinal.ganador.nombre;
    }
    if (ganadorGranFinalId) {
      if (ganadorGranFinalId === (granFinal.jugador1?._id || granFinal.jugador1)) {
        return granFinal.nombreJugador1 || 'Ganador Winner';
      }
      if (ganadorGranFinalId === (granFinal.jugador2?._id || granFinal.jugador2)) {
        return granFinal.nombreJugador2 || 'Ganador Loser';
      }
    }
    return null;
  })();

  // Ganador de eliminación simple (viene del ganador de W-Final cuando destino es CAMPEON)
  // El backend guarda esto en campeonato.resultados.campeon
  const campeonSimple = bracketData?.resultados?.campeon || bracketData?.campeon;
  const campeonSimpleId = typeof campeonSimple === 'string' ? campeonSimple : campeonSimple?._id;
  const campeonSimpleNombre = typeof campeonSimple === 'string' 
    ? null 
    : (campeonSimple?.username || campeonSimple?.nombre || bracketData?.resultados?.nombreCampeon);

  // Determinar el campeón final (prioriza Gran Final para doble eliminación)
  const hayGanadorGranFinal = granFinal?.completada && ganadorGranFinalId && ganadorGranFinalNombre;
  const hayGanadorSimple = esEliminacionSimple && campeonSimpleId;
  
  const campeonFinalId = hayGanadorGranFinal ? ganadorGranFinalId : (hayGanadorSimple ? campeonSimpleId : null);
  const campeonFinalNombre = hayGanadorGranFinal ? ganadorGranFinalNombre : (hayGanadorSimple ? campeonSimpleNombre : null);
  const hayGanadorFinal = campeonFinalId && campeonFinalNombre;

  // Mostrar modal de campeón automáticamente cuando hay ganador
  useEffect(() => {
    if (hayGanadorFinal) {
      setShowCampeonModal(true);
    }
  }, [hayGanadorFinal]);

  // Estados vacíos
  if (!bracketData?.bracket) {
    return (
      <div className="bracket-empty">
        <p>El bracket aún no ha sido generado</p>
      </div>
    );
  }

  const tieneWinner = bracketData.bracket.winner?.rondas && 
    Object.keys(bracketData.bracket.winner.rondas).length > 0;
  const tieneLoser = bracketData.bracket.loser?.rondas && 
    Object.keys(bracketData.bracket.loser.rondas).length > 0;

  // Calcular ranking de participantes por posición final del torneo
  const calcularRanking = () => {
    if (!bracketData.participantes) return [];
    
    // Filtrar solo los que han jugado al menos una partida
    const participantesConPartidas = bracketData.participantes
      .filter(p => p.estadisticas && p.estadisticas.totalPartidas > 0)
      .map(p => ({
        nombre: p.nombreUsuario || p.usuario?.username || 'Jugador',
        posicionFinal: p.posicionFinal,
        eliminado: p.eliminado,
        average: p.estadisticas.averageTotal || 0,
        total180s: p.estadisticas.total180s || 0,
        partidasJugadas: p.estadisticas.totalPartidas || 0,
        partidasGanadas: p.estadisticas.partidasGanadas || 0,
        mejorAverage: p.estadisticas.mejorAverage || 0,
      }));
    
    // Ordenar: primero por posición final (1º, 2º, 3º...), luego los que siguen activos por partidas ganadas
    return participantesConPartidas.sort((a, b) => {
      // Si ambos tienen posición final, ordenar por posición (menor = mejor)
      if (a.posicionFinal && b.posicionFinal) {
        return a.posicionFinal - b.posicionFinal;
      }
      // Los que tienen posición final (eliminados) van después de los activos
      if (a.posicionFinal && !b.posicionFinal) return 1;
      if (!a.posicionFinal && b.posicionFinal) return -1;
      // Para los activos (sin posición final), ordenar por partidas ganadas
      if (b.partidasGanadas !== a.partidasGanadas) {
        return b.partidasGanadas - a.partidasGanadas;
      }
      // Si empatan en partidas ganadas, ordenar por average
      return b.average - a.average;
    });
  };

  const ranking = calcularRanking();
  
  // Calcular posición visual del ranking (permite empates)
  const getPosicionVisual = (idx) => {
    if (idx === 0) return 1;
    const jugadorActual = ranking[idx];
    const jugadorAnterior = ranking[idx - 1];
    
    // Si tienen la misma posición final, misma posición visual
    if (jugadorActual.posicionFinal && jugadorAnterior.posicionFinal && 
        jugadorActual.posicionFinal === jugadorAnterior.posicionFinal) {
      // Buscar recursivamente la posición del anterior
      return getPosicionVisual(idx - 1);
    }
    
    return idx + 1;
  };

  // Modal de estadísticas de partida
  const renderModalEstadisticas = () => {
    if (!partidaSeleccionada) return null;
    
    const p = partidaSeleccionada;
    const j1Nombre = p.nombreJugador1 || p.jugador1?.username || 'Jugador 1';
    const j2Nombre = p.nombreJugador2 || p.jugador2?.username || 'Jugador 2';
    const statsEmbebidos = p.estadisticasPartida || p.estadisticas || p.stats || { jugador1: {}, jugador2: {} };
    const detalleCompatible = p.partidaId && detallePartida && String(detallePartida._id) === String(p.partidaId)
      ? detallePartida
      : null;
    const ganadorId = getEntityId(p.ganador);
    const jugador1Id = getEntityId(p.jugador1);
    const jugador2Id = getEntityId(p.jugador2);
    const j1Ganador = ganadorId && String(ganadorId) === String(jugador1Id);
    const j2Ganador = ganadorId && String(ganadorId) === String(jugador2Id);
    const averageJugador1 = typeof detalleCompatible?.promedioAveragesPartida?.jugador1 === 'number'
      ? detalleCompatible.promedioAveragesPartida.jugador1
      : Number(statsEmbebidos?.jugador1?.average);
    const averageJugador2 = typeof detalleCompatible?.promedioAveragesPartida?.jugador2 === 'number'
      ? detalleCompatible.promedioAveragesPartida.jugador2
      : Number(statsEmbebidos?.jugador2?.average);
    const count180Jugador1 = typeof detalleCompatible?.count180s?.jugador1 === 'number'
      ? detalleCompatible.count180s.jugador1
      : Number(statsEmbebidos?.jugador1?.count180s) || 0;
    const count180Jugador2 = typeof detalleCompatible?.count180s?.jugador2 === 'number'
      ? detalleCompatible.count180s.jugador2
      : Number(statsEmbebidos?.jugador2?.count180s) || 0;
    // Los datos avanzados (100+, 140+, checkout%, high finish) vienen embebidos en la propia
    // partida del bracket; usamos la partida enlazada (detalleCompatible) solo si trae algo mejor.
    const tieneAvanzadasEmbebidas = ['jugador1', 'jugador2'].some(key => {
      const j = statsEmbebidos?.[key];
      return j && (
        j.count100Plus !== undefined
        || j.count140Plus !== undefined
        || j.checkoutOpportunities !== undefined
        || j.checkoutSuccess !== undefined
        || j.highFinish !== undefined
      );
    });
    const advancedStats = detalleCompatible?.estadisticasAvanzadas
      || (tieneAvanzadasEmbebidas ? statsEmbebidos : null);
    const advancedStatsDisponibles = Boolean(advancedStats);
    const statsItems = [
      {
        label: 'RESULTADO',
        player1Value: String(p.resultado?.jugador1Sets ?? 0),
        player2Value: String(p.resultado?.jugador2Sets ?? 0),
        primary: true,
      },
      {
        label: 'AVRG',
        player1Value: formatMatchStatValue(averageJugador1, { decimals: 1, fallback: '0.0' }),
        player2Value: formatMatchStatValue(averageJugador2, { decimals: 1, fallback: '0.0' }),
      },
      {
        label: '180',
        player1Value: String(count180Jugador1),
        player2Value: String(count180Jugador2),
        highlight: true,
      },
      {
        label: '100+',
        player1Value: advancedStatsDisponibles ? String(advancedStats?.jugador1?.count100Plus || 0) : '-',
        player2Value: advancedStatsDisponibles ? String(advancedStats?.jugador2?.count100Plus || 0) : '-',
      },
      {
        label: '140+',
        player1Value: advancedStatsDisponibles ? String(advancedStats?.jugador1?.count140Plus || 0) : '-',
        player2Value: advancedStatsDisponibles ? String(advancedStats?.jugador2?.count140Plus || 0) : '-',
      },
      {
        label: 'Checkout %',
        player1Value: advancedStatsDisponibles
          ? formatMatchStatValue(Number(advancedStats?.jugador1?.checkoutPct), { decimals: 1, suffix: '%' })
          : '-',
        player2Value: advancedStatsDisponibles
          ? formatMatchStatValue(Number(advancedStats?.jugador2?.checkoutPct), { decimals: 1, suffix: '%' })
          : '-',
      },
      {
        label: 'High Finish',
        player1Value: advancedStatsDisponibles && (advancedStats?.jugador1?.highFinish || 0) > 0
          ? String(advancedStats.jugador1.highFinish)
          : '-',
        player2Value: advancedStatsDisponibles && (advancedStats?.jugador2?.highFinish || 0) > 0
          ? String(advancedStats.jugador2.highFinish)
          : '-',
      },
    ];
    
    return (
      <div className="stats-modal-overlay" onClick={closePartidaModal}>
        <div className="stats-modal" onClick={e => e.stopPropagation()}>
          <div className="stats-modal-header">
            <h3>📊 Estadísticas del Encuentro</h3>
            <button className="modal-close" onClick={closePartidaModal}>×</button>
          </div>
          
          <div className="stats-modal-content">
            <div className="stats-match-info">
              <span className="stats-ronda">{p.ronda}</span>
              <span className="stats-resultado">
                {p.resultado?.jugador1Sets} - {p.resultado?.jugador2Sets}
              </span>
            </div>

            <div className="stats-modal-summary">
              <div className="partida-scoreline stats-modal-scoreline">
                <span className={`partida-scoreline-player left ${j1Ganador ? 'winner' : ''}`}>
                  {j1Ganador && <span className="winner-badge">🏆</span>}
                  {j1Nombre}
                </span>
                <span className="partida-scoreline-score">{p.resultado?.jugador1Sets ?? 0}</span>
                <span className="partida-scoreline-vs">vs</span>
                <span className="partida-scoreline-score">{p.resultado?.jugador2Sets ?? 0}</span>
                <span className={`partida-scoreline-player right ${j2Ganador ? 'winner' : ''}`}>
                  {j2Nombre}
                  {j2Ganador && <span className="winner-badge">🏆</span>}
                </span>
              </div>

              {loadingDetallePartida && (
                <div className="stats-modal-loading" role="status" aria-label="Cargando estadísticas avanzadas">
                  <span className="stats-modal-loading-spinner" aria-hidden="true"></span>
                  <span className="stats-modal-loading-text">Cargando estadísticas avanzadas…</span>
                </div>
              )}

              <div className="stats-modal-stats">
                {statsItems.map((item) => {
                  const valueClasses = [
                    item.highlight ? 'highlight' : '',
                    item.primary ? 'primary' : '',
                  ].filter(Boolean).join(' ');

                  return (
                    <div key={item.label} className={`partida-stat-row ${item.primary ? 'primary' : ''}`}>
                      <span className={`partida-stat-value left ${valueClasses} ${j1Ganador && item.primary ? 'winner' : ''}`.trim()}>
                        {item.player1Value}
                      </span>
                      <span className={`partida-stat-label ${item.highlight ? 'highlight' : ''} ${item.primary ? 'primary' : ''}`.trim()}>
                        {item.label}
                      </span>
                      <span className={`partida-stat-value right ${valueClasses} ${j2Ganador && item.primary ? 'winner' : ''}`.trim()}>
                        {item.player2Value}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="bracket-viewer-container" style={layoutVars}>
      {/* Controles */}
      <div className="bracket-controls">
        {/* Solo mostrar toggle Winner/Loser en doble eliminación */}
        {!esEliminacionSimple && (
          <div className="bracket-toggle">
            <button 
              className={`toggle-btn ${modoVista === 'winner' ? 'active winner' : ''}`}
              onClick={() => setModoVista('winner')}
              disabled={!tieneWinner}
            >
              🏆 Winner
            </button>
            <button 
              className={`toggle-btn ${modoVista === 'loser' ? 'active loser' : ''}`}
              onClick={() => setModoVista('loser')}
              disabled={!tieneLoser}
            >
              ⚠️ Loser
            </button>
          </div>
        )}
        
        {/* Badge de eliminación simple */}
        {esEliminacionSimple && (
          <div className="bracket-badge-simple">
            ⚡ Eliminación Simple
          </div>
        )}
        
        {userId && (
          <button className="buscar-btn" onClick={buscarMiPosicion}>
            🔍 Búscate
          </button>
        )}
      </div>

      {/* Info */}
      <div className="bracket-info">
        <span className="bracket-tipo">
          {esEliminacionSimple 
            ? '⚡ Eliminación Simple' 
            : modoVista === 'winner' 
              ? '🏆 Winner Bracket' 
              : '⚠️ Loser Bracket'}
        </span>
        <span className="bracket-estado">
          Ronda {bracketData.bracket[modoVista]?.rondaActual || 1} de{' '}
          {bracketData.bracket[modoVista]?.totalRondas || 1}
        </span>
        <span className="bracket-formato">
          🎯 {getBracketFormatLabel({
            modoVista,
            tipoEliminacion: bracketData?.info?.tipoEliminacion,
            configuracion: bracketData?.configuracion,
          })}
        </span>
      </div>

      {/* Bracket scrollable */}
      <div className="bracket-scroll-container" ref={scrollContainerRef}>
        {renderBracket()}
      </div>

      {/* Ranking del Torneo */}
      {ranking.length > 0 && (
        <div className="bracket-ranking-section">
          <h3 className="ranking-title">🏆 Clasificación del Torneo</h3>
          <p className="ranking-subtitle">Posiciones según resultado en el torneo</p>
          <div className="ranking-table-container">
            <table className="ranking-table">
              <thead>
                <tr>
                  <th>Pos</th>
                  <th>Jugador</th>
                  <th>Estado</th>
                  <th>Avg</th>
                  <th>180s</th>
                  <th title="Ganadas - Perdidas">G-P</th>
                </tr>
              </thead>
              <tbody>
                {ranking.map((jugador, idx) => {
                  const posVisual = getPosicionVisual(idx);
                  const medallaOrNum = posVisual === 1 ? '🥇' : posVisual === 2 ? '🥈' : posVisual === 3 ? '🥉' : posVisual;
                  const estado = jugador.posicionFinal 
                    ? `${jugador.posicionFinal}º` 
                    : '🎯 En juego';
                  const partidasPerdidas = jugador.partidasJugadas - jugador.partidasGanadas;
                  
                  return (
                    <tr key={idx} className={`${posVisual <= 3 ? 'top-player' : ''} ${!jugador.posicionFinal ? 'active-player' : ''}`}>
                      <td className="rank-position">{medallaOrNum}</td>
                      <td className="rank-name">{jugador.nombre}</td>
                      <td className="rank-estado">{estado}</td>
                      <td className="rank-avg">{jugador.average.toFixed(2)}</td>
                      <td className="rank-180s">{jugador.total180s}</td>
                      <td className="rank-record" title={`${jugador.partidasGanadas} ganadas - ${partidasPerdidas} perdidas`}>{jugador.partidasGanadas}-{partidasPerdidas}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Leyenda */}
      <div className="bracket-leyenda">
        <span className="leyenda-item">
          <span className="leyenda-color ganador"></span> Ganador
        </span>
        <span className="leyenda-item">
          <span className="leyenda-color perdedor"></span> Eliminado
        </span>
        <span className="leyenda-item">
          <span className="leyenda-color usuario"></span> Tú
        </span>
        <span className="leyenda-item">
          <span className="leyenda-color pendiente"></span> Pendiente
        </span>
      </div>

      {/* Modal de estadísticas */}
      {renderModalEstadisticas()}

      {/* Modal de Campeón */}
      {showCampeonModal && hayGanadorFinal && (
        <div className="campeon-modal-overlay" onClick={() => setShowCampeonModal(false)}>
          <div className="campeon-modal" onClick={e => e.stopPropagation()}>
            <div className="campeon-modal-content">
              <div className="campeon-trophy">🏆</div>
              <h2 className="campeon-titulo">¡Tenemos Campeón!</h2>
              <div className="campeon-nombre">{campeonFinalNombre}</div>
              <p className="campeon-subtitulo">ha ganado el torneo</p>
              {campeonatoNombre && (
                <p className="campeon-torneo">{campeonatoNombre}</p>
              )}
              <button className="campeon-cerrar-btn" onClick={() => setShowCampeonModal(false)}>
                Ver Bracket
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BracketViewer;
